// The LIB catalogue console route subtree (console slice 2). Code-based routes
// per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()` +
// `createLazyRoute`. This factory hangs the `/library/catalogue` tree off the
// portal's root route so router.tsx stays the single wiring point.
//
// GATING is per-screen (router.tsx's documented choice): the catalogue LAYOUT
// route re-checks lib:catalog.read via useSession/<Show> before it renders any
// child — a defence-in-depth mirror of the API's rbac('lib:catalog.read') guard
// on every catalogue read route (the UI can only HIDE; the gateway is the
// authority, ADR-001 §14.2). Anonymous → /login; signed-in-without-the-grant →
// an explicit denied panel, never a blank Outlet.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { libraryQueries } from "../../index.js";
import { BOOK_SORTS, BOOK_STATUSES, MATERIAL_TYPES } from "../../../schemas/index.js";
import type { BookSort, BookStatus, MaterialType } from "../../../schemas/index.js";
import { HubGrid } from "@erp/ui/hub-grid";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { PageHeading } from "@erp/ui/page-heading";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { BookOpen } from "lucide-solid";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ─────────────────────────── AllBooks URL-as-state ───────────────────────────
 * The browse view's filters + sort live in the URL (DEV-GUIDE-002 §4.1).
 * The guide's ideal is a Zod-direct validateSearch with `.catch()`; zod is not a
 * direct dependency of this portal (and this slice may not run `pnpm install`),
 * so this is a hand-written validator with the SAME self-healing contract — a
 * garbage `?status=` / `?sort=` heals to the default rather than throwing. Swap
 * for `BookSort.catch(...)` once zod is a portal dep.
 *
 * NOTE the FTS omnibox `q` is deliberately NOT here: it drives the dedicated
 * capped GET /catalogue/search path (typeahead), kept as local screen state so
 * the loader stays the plain keyset books list. `q` present → the screen shows
 * search results; `q` empty → the URL-filtered keyset list. */
export const BOOKS_DEFAULT_SORT: BookSort = "title:asc";
export const BOOKS_PAGE_SIZE = 20;

/**
 * NO `cursor` (2026-09-22). `/library/catalogue/books` is an INFINITE stream
 * now: every facet, the sort and the search still live in the URL — a shared
 * link restores exactly the list someone was looking at — but the cursor does
 * not, because it no longer names a page. It names a position inside one
 * accumulating list, and a link to "everything up to cursor X" is a link to a
 * different amount of scrolling for every reader. A stale `?cursor=` on an old
 * bookmark simply falls out here, which is the self-healing contract this
 * validator already had.
 */
export interface BooksSearch {
  subject?: string;
  library?: string;
  author?: string;
  publisher?: string;
  materialType?: MaterialType;
  /** Inclusive publication-year window; either end may stand alone. */
  yearFrom?: number;
  yearTo?: number;
  format?: "print" | "ebook";
  status?: BookStatus;
  sort?: BookSort;
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** A 4-digit-ish year from the URL — anything else heals to "no bound". */
function optionalYear(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= 9999 ? n : undefined;
}

export function validateBooksSearch(input: Record<string, unknown>): BooksSearch {
  const status = BOOK_STATUSES.includes(input.status as BookStatus) ? (input.status as BookStatus) : undefined;
  const sort = BOOK_SORTS.includes(input.sort as BookSort) ? (input.sort as BookSort) : undefined;
  const materialType = MATERIAL_TYPES.includes(input.materialType as MaterialType)
    ? (input.materialType as MaterialType)
    : undefined;
  const format = input.format === "print" || input.format === "ebook" ? input.format : undefined;
  return {
    subject: optionalString(input.subject),
    library: optionalString(input.library),
    author: optionalString(input.author),
    publisher: optionalString(input.publisher),
    materialType,
    yearFrom: optionalYear(input.yearFrom),
    yearTo: optionalYear(input.yearTo),
    format,
    status,
    sort,
  };
}

/* ───────────────────────── panel URL contract (closed sets) ─────────────────
 * Every editing/viewing surface in this subtree is a URL-addressed SidePanel
 * (`?panel=…&panelId=…&panelWide=1`), so Back closes it and a pasted link opens
 * straight into the record. The name set is CLOSED per route — `panelSearch`
 * heals anything else to "no panel", which is why a stale or hand-typed param
 * can never render an empty form. */
export const SUBJECT_PANELS = ["new", "edit", "tag"] as const;
export const PUBLISHER_PANELS = ["new", "edit"] as const;
export const AUTHOR_PANELS = ["new", "edit", "merge"] as const;
export const COPY_PANELS = ["edit"] as const;
/* The two authority DETAIL screens are Card stacks with no tab strip, so their
 * one panel is the audit trail: ?panel=history, deep-linkable and Back-closable
 * like every other panel in the subtree. */
export const AUTHOR_DETAIL_PANELS = ["history"] as const;
export const PUBLISHER_DETAIL_PANELS = ["history"] as const;
/** The book DETAIL route's own closed set: the per-copy edit panel plus the
 *  record's audit trail (`?panel=history`). Kept beside COPY_PANELS rather than
 *  widening it — "edit" is a copy-scoped name, "history" is title-scoped, and
 *  only this route may open either. */
export const BOOK_DETAIL_PANELS = ["edit", "history", "addcopies"] as const;

/** The book detail's search contract: the URL-owned tab strip plus the panel trio. */
export interface BookDetailSearch extends PanelSearch {
  /** The open tab. "details" is the default and ABSENT from the URL (the AR/finance idiom); only "copies" is written. */
  tab?: "copies";
}

/**
 * The book DETAIL route's validator (2026-09-19, audit C-05 — the tab strip is
 * the kit's Tabs and the URL owns it, so a shared link, a refresh and Back all
 * land on the same panel). One heal beyond `panelSearch`: a COPY-scoped panel
 * (`?panel=edit&panelId=…`, `?panel=addcopies`) lives inside the Copies tab's
 * subtree, so an open one IMPLIES `tab=copies` — a pasted link that names the
 * panel but not the tab still opens onto a mounted surface. The router runs the
 * validators over every navigation's RESULT, so the Add-copy button's one
 * navigation (`panel=addcopies`, its own keys only) lands in the URL as
 * `?tab=copies&panel=addcopies`, and closing the panel keeps the desk on the
 * Copies tab over the rows it just added (admin book-detail.test.tsx pins it).
 */
export function validateBookDetailSearch(input: Record<string, unknown>): BookDetailSearch {
  const panel = panelSearch(BOOK_DETAIL_PANELS, input);
  const copyPanel = panel.panel === "edit" || panel.panel === "addcopies";
  return { tab: input.tab === "copies" || copyPanel ? "copies" : undefined, ...panel };
}
/** Bulk add's one panel: the staged ROW's full looked-up detail, addressed by
 *  its row key (`?panel=row&panelId=<key>`). Rows are local staging state, so
 *  the screen additionally heals an id that matches no staged row to closed. */
/* `row` reads a staged row; `edit` edits one. Two names rather than a mode flag
   on one, so a pasted link says which surface it opens and Back closes exactly
   the one that was opened. */
export const BULK_ADD_PANELS = ["row", "edit"] as const;

const CATALOGUE_LINKS = [
  { to: "/library/catalogue/books", label: "Books", blurb: "Titles, copies and availability", id: "catalogue-nav-books" },
  { to: "/library/catalogue/authors", label: "Authors", blurb: "Authority registry + merge", id: "catalogue-nav-authors" },
  { to: "/library/catalogue/publishers", label: "Publishers", blurb: "Imprint registry", id: "catalogue-nav-publishers" },
  { to: "/library/catalogue/subjects", label: "Subjects & Tags", blurb: "Classification registries", id: "catalogue-nav-subjects" },
] as const;

/**
 * The catalogue shell: gates on lib:catalog.read, then renders the active child.
 *
 * The gating sequence (loading → unreachable → session → grant) now lives in
 * @erp/ui/console-layout, identically for all eight consoles. The horizontal
 * tab strip that used to sit here is gone: Books/Authors/Publishers/Subjects
 * are sidebar entries in modules/lib's manifest, and the shell header shows the
 * trail — so wayfinding is the same everywhere instead of per-console. The
 * <main> landmark and page padding belong to AppShell now.
 */
function CatalogueLayout() {
  return (
    <ConsoleLayout
      perm="lib:catalog.read"
      deniedMessage="This account cannot open the catalogue. Ask an administrator for catalogue access."
      skeletonId="catalogue-skeleton"
      deniedId="catalogue-denied"
      unreachableId="catalogue-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/catalogue` index — the landing with cards into each registry. */
function CatalogueLanding() {
  return (
    <div>
      {/* The console's identity capsule (DEV-GUIDE-002 §12.5): every catalogue
          screen wears indigo, so the landing they hang off wears it too. */}
      <PageHeading
        icon={BookOpen}
        hue={MODULE_HUE.catalogue}
        title="Catalogue"
        subtitle="Books, copies, and the author, publisher and subject registries"
        help={{
          title: "What the catalogue holds",
          children: (
            <p>
              Every title the library owns, and the name lists that describe it. Open Books to find a title, see
              its copies and where each one sits; Authors, Publishers and Subjects &amp; Tags are the shared
              registries a record picks from, so a name is spelt one way everywhere and can be merged when it is
              not. Each card opens one register, and the colour on this page follows you through the catalogue.
            </p>
          ),
        }}
      />
      {/* Was a hand-drawn grid of Link > Card > CardHeader with a green
          `StatusBadge label="Open"` for a button — see @erp/ui/hub-grid for
          why both were wrong, and for the one place that shape now lives. */}
      <HubGrid id="catalogue-landing" items={CATALOGUE_LINKS} hue={MODULE_HUE.catalogue} columns={4} />
    </div>
  );
}

/**
 * Build the `/library/catalogue` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Screens
 * close their own `.lazy()` loop with `createLazyRoute('/library/catalogue/<x>')`.
 */
export function makeLibCatalogueRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const catalogueRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/catalogue",
    component: CatalogueLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "/",
    component: CatalogueLanding,
  });

  const booksRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books",
    validateSearch: validateBooksSearch, // self-healing (§4.1); see the note above
    loaderDeps: ({ search }) => ({
      subject: search.subject,
      library: search.library,
      author: search.author,
      publisher: search.publisher,
      materialType: search.materialType,
      yearFrom: search.yearFrom,
      yearTo: search.yearTo,
      format: search.format,
      status: search.status,
      sort: search.sort,
    }),
    loader: ({ context, deps }) => {
      // ONE definition serves loader + screen (§4.3 Do). The filters object here
      // MUST equal the screen's so both hit the same cache entry. Warmed, not
      // awaited (audit UX-01): an awaited ensure held the route until the page
      // arrived — /library/catalogue/books rendered NOTHING for the whole API
      // wait; prefetching returns at once and AllBooks paints its heading, its
      // stat bones and its table bones from `queryState(...).pending()`.
      //
      // `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default
      // is 1) into the same accumulating entry the screen then reads, so the
      // warm-not-awaited rule above is unchanged and the reader's scroll fetches
      // page two, never the loader.
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.booksInfinite({
          limit: BOOKS_PAGE_SIZE,
          sort: deps.sort ?? BOOKS_DEFAULT_SORT,
          subject: deps.subject,
          library: deps.library,
          author: deps.author,
          publisher: deps.publisher,
          materialType: deps.materialType,
          yearFrom: deps.yearFrom,
          yearTo: deps.yearTo,
          format: deps.format,
          status: deps.status,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/AllBooks.js")));

  // Static `books/new` is registered before the dynamic `books/$bookId` (path
  // ranking makes static win regardless, but the order documents intent).
  const bookNewRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books/new",
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/BookForm.js")));

  const bookDetailRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books/$bookId",
    // ?tab=copies selects the Copies tab; that tab edits a copy in a URL-addressed
    // panel (?panel=edit&panelId=<barcode>) and ?panel=history opens the title's
    // audit trail on the same contract. See validateBookDetailSearch for the heal.
    validateSearch: validateBookDetailSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/BookDetail.js")));

  const bookEditRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "books/$bookId/edit",
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/BookForm.js"), (m) => m.EditRoute));

  // Bulk add — registered before the dynamic books/$bookId sibling for the same
  // documented reason `books/new` is: ranking already makes static win, the
  // order states the intent.
  const bulkAddRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "bulk-add",
    // Each staged row's read-only detail opens as ?panel=row&panelId=<row key>.
    validateSearch: (input: Record<string, unknown>) => panelSearch(BULK_ADD_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/BulkAddBooks.js")));

  const authorsRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "authors",
    validateSearch: (input: Record<string, unknown>) => panelSearch(AUTHOR_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/Authors.js")));

  const marcImportRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "marc-import",
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/MarcImport.js")));

  const authorDetailRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "authors/$authorId",
    // The record's audit trail opens in a URL-addressed panel (?panel=history).
    validateSearch: (input: Record<string, unknown>) => panelSearch(AUTHOR_DETAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/AuthorDetail.js")));

  const publishersRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "publishers",
    validateSearch: (input: Record<string, unknown>) => panelSearch(PUBLISHER_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/Publishers.js")));

  const publisherDetailRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "publishers/$publisherId",
    // Same contract as the author detail: ?panel=history and nothing else.
    validateSearch: (input: Record<string, unknown>) => panelSearch(PUBLISHER_DETAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/PublisherDetail.js")));

  const subjectsRoute = createRoute({
    getParentRoute: () => catalogueRoute,
    path: "subjects",
    validateSearch: (input: Record<string, unknown>) => panelSearch(SUBJECT_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/catalogue/Subjects.js")));

  return [
    catalogueRoute.addChildren([
      indexRoute,
      booksRoute,
      bookNewRoute,
      bookDetailRoute,
      bookEditRoute,
      bulkAddRoute,
      marcImportRoute,
      authorsRoute,
      authorDetailRoute,
      publishersRoute,
      publisherDetailRoute,
      subjectsRoute,
    ]),
  ];
}
